import { defineStore } from "pinia";
import { privateBoaHttp, honoClient } from "@/boa-api-client";
import { HTTPError } from "ky";
import {
  type ProductParent,
  type AnnualSponsorship,
  type CeremonyTicket,
  type MagazineAd,
  type MagazineArticle,
  type Magazine,
  type MagazineDeposit,
  type Plaque,
  type VotingStageSponsorship,
  type VoterRewards,
  type MagazineAdDeposit,
  type Upgrade,
  isProductParent,
  type Product,
  isUpgrade,
  isVoterRewards,
} from "best-of-awards-api";

import {
  isAnnualSponsorship,
  isCeremonyTicket,
  isGeneralCeremonyTicket,
  isMagazine,
  isMagazineAd,
  isMagazineAdDeposit,
  isMagazineArticle,
  isMagazineDeposit,
  isPlaque,
  isVIPCeremonyTicket,
  isVotingStageSponsorship,
} from "best-of-awards-api";

import { isAfter, isBefore, parseISO } from "date-fns";
import { useAwardStore } from "@/stores";
import { errorToast } from "@/util/toast";

type State = {
  products: Array<Product>;
  loading: boolean;
};

export default defineStore("products", {
  state: (): State => ({
    products: [],
    loading: false,
  }),
  getters: {
    currentProducts(state): Product[] {
      return state.products.filter((p) => !p.archived);
    },
    activeProducts(state): Product[] {
      const now = new Date();
      return state.products.filter(
        (p) =>
          !p.archived &&
          p.active &&
          (p.availability === "Always" ||
            (p.datesAvailable &&
              isAfter(now, parseISO(p.datesAvailable[0])) &&
              isBefore(now, parseISO(p.datesAvailable[1]))))
      );
      // .map((p) => {
      //   if (!p.unlimited && !p.quantityAvailable) {
      //     p.soldOut = true;
      //   }
      //   return p;
      // });
    },
    annualSponsorship(): AnnualSponsorship | undefined {
      const sponsorship = this.activeProducts.find(isAnnualSponsorship);
      if (sponsorship) {
        const awardStore = useAwardStore();
        return {
          ...sponsorship,
          options: sponsorship.options.filter(
            (o) => o.awardYear === awardStore.awardYear
          ),
        };
      }
    },
    votingStageSponsorship(): VotingStageSponsorship | undefined {
      const sponsorship = this.activeProducts.find(isVotingStageSponsorship);
      if (sponsorship) {
        const awardStore = useAwardStore();
        return {
          ...sponsorship,
          options: sponsorship.options.filter(
            (o) => o.awardYear === awardStore.awardYear
          ),
        };
      }
    },
    voterRewards(): VoterRewards | undefined {
      const voterRewards = this.activeProducts.find(isVoterRewards);
      if (voterRewards) {
        const awardStore = useAwardStore();
        return {
          ...voterRewards,
          options: voterRewards.options.filter(
            (o) => o.awardYear === awardStore.awardYear
          ),
        };
      }
    },
    magazines(): Magazine | undefined {
      const magazine = this.activeProducts.find(isMagazine);
      if (magazine) {
        magazine.options.sort((a, b) => b.awardYear - a.awardYear);
        return magazine;
      }
    },
    magazineDeposit(): MagazineDeposit | undefined {
      const deposit = this.activeProducts.find(isMagazineDeposit);
      if (deposit) {
        const awardStore = useAwardStore();
        return {
          ...deposit,
          options: deposit.options.filter(
            (o) => o.awardYear === awardStore.awardYear
          ),
        };
      }
    },
    magazineAd(): MagazineAd | undefined {
      const ad = this.activeProducts.find(isMagazineAd);
      if (ad) {
        const awardStore = useAwardStore();
        return {
          ...ad,
          options: ad.options.filter(
            (o) => o.awardYear === awardStore.awardYear
          ),
        };
      }
    },
    magazineAdDeposit(): MagazineAdDeposit | undefined {
      const deposit = this.activeProducts.find(isMagazineAdDeposit);
      if (deposit) {
        const awardStore = useAwardStore();
        return {
          ...deposit,
          options: deposit.options.filter(
            (o) => o.awardYear === awardStore.awardYear
          ),
        };
      }
    },
    magazineArticle(): MagazineArticle | undefined {
      const article = this.activeProducts.find(isMagazineArticle);
      if (article) {
        const awardStore = useAwardStore();
        return {
          ...article,
          options: article.options.filter(
            (o) => o.awardYear === awardStore.awardYear
          ),
        };
      }
    },
    plaque(): Plaque | undefined {
      const plaque = this.activeProducts.find(isPlaque);
      if (plaque) {
        plaque.options.sort((a, b) => (b.awardYear ?? 0) - (a.awardYear ?? 0));
        return plaque;
      }
    },
    ceremonyTicket(): CeremonyTicket | undefined {
      const ticket = this.activeProducts.find(isCeremonyTicket);
      if (!ticket) {
        return;
      }
      const awardStore = useAwardStore();
      for (const ticketOption of ticket.options) {
        if (
          (!isGeneralCeremonyTicket(ticketOption) &&
            !isVIPCeremonyTicket(ticketOption)) ||
          ticketOption.awardYear !== awardStore.awardYear
        ) {
          continue;
        }
        const { nights = [] } = ticketOption;
        for (const night of nights) {
          if (night.originalQuantityAvailable > 0 && !night.quantityAvailable) {
            night.soldOut = true;
          }
        }
      }
      return ticket;
    },
    upgrades(): Upgrade | undefined {
      return this.activeProducts.find(isUpgrade);
    },
  },
  actions: {
    async getProducts() {
      this.loading = true;
      const response = await honoClient.products.$get();
      if (!response.ok) {
        throw new Error(response.statusText);
      }
      const products = await response.json();
      this.products = products;
      this.loading = false;
      return products;
    },
    async createProduct(
      product: Partial<ProductParent>
    ): Promise<ProductParent | { message: string }> {
      try {
        const response: { product: unknown } = await privateBoaHttp
          .put("products", {
            json: { product },
          })
          .json();
        if (!isProductParent(response.product)) {
          throw new Error("Failed to create product.");
        }
        return response.product;
      } catch (error) {
        console.error(error);
        if (error instanceof HTTPError) {
          const response: { message: string } = await error.response.json();
          return { message: response.message };
        }
        return { message: "An unknown error occurred" };
      }
    },
    async updateProduct(
      product: Product
    ): Promise<{ product?: ProductParent; message?: string }> {
      const response = await honoClient.products.$post({
        json: product,
      });
      if (!response.ok) {
        errorToast("An unknown error occurred.", { timeout: 5000 });
        throw new Error(response.statusText);
      }
      const { data: updatedProduct } = await response.json();
      return { product: updatedProduct };
    },
    clear() {
      this.$reset();
    },
  },
});
